import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '@shared/services/api';
import { Card, Button } from '@shared/components/ui';
import { CheckCircle2, XCircle, RefreshCw, Mail, ArrowRight } from 'lucide-react';

export default function VerifyEmail() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  // Flag para prevenir chamadas duplicadas (React Strict Mode)
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevenir execução duplicada em React Strict Mode
    if (hasVerified.current) {
      return;
    }

    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage(t('verifyEmail.errors.missingToken'));
        return;
      }

      // Marcar como iniciado para prevenir dupla execução
      hasVerified.current = true;

      try {
        const response = await authService.verifyEmail(token);
        setStatus('success');
        setMessage(t('verifyEmail.successMessage'));
        // Passar email para login via query parameter
        const emailParam = response.email ? `&email=${encodeURIComponent(response.email)}` : '';
        setTimeout(() => navigate(`/login?message=verified${emailParam}`, { replace: true }), 3000);
      } catch (err: any) {
        // Verificar se é erro de "email já verificado" - nesse caso, tratar como sucesso
        const errorData = err.response?.data;
        const errorMessage = String(errorData?.error || '').toLowerCase();

        if (
          errorMessage.toLowerCase().includes('already verified') ||
          errorMessage.toLowerCase().includes('já verificado')
        ) {
          setStatus('success');
          setMessage(t('verifyEmail.successMessage'));
          // Tentar extrair email do erro se disponível
          const errorEmail = errorData?.email;
          const emailParam = errorEmail ? `&email=${encodeURIComponent(errorEmail)}` : '';
          setTimeout(
            () => navigate(`/login?message=verified${emailParam}`, { replace: true }),
            3000
          );
          return;
        }

        setStatus('error');
        setMessage(t('verifyEmail.errors.failed'));
      }
    };

    verifyEmail();
  }, [searchParams, navigate, t]);

  const handleResendEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (resendStatus === 'sending') return;

    setResendStatus('sending');
    try {
      await authService.resendVerificationEmail({ email: resendEmail.trim() });
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-surface-light dark:bg-surface-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-in fade-in zoom-in duration-500">
        <div className="mb-8 text-center sm:mb-10">
          <div className="w-16 h-16 bg-brand-600 rounded-2xl flex items-center justify-center mx-auto shadow-xl shadow-brand-200 rotate-3">
            <Mail className="text-white dark:text-on-accent-dark w-8 h-8" strokeWidth={2.5} />
          </div>
          <h1 className="mt-6 text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
            pgkb <span className="text-brand-600 dark:text-brand-400">Vault</span>
          </h1>
        </div>

        <Card className="space-y-8 p-6 text-center shadow-elevated sm:p-10">
          {status === 'loading' && (
            <div className="space-y-6">
              <div className="relative w-20 h-20 mx-auto">
                <div className="absolute inset-0 border-4 border-surface-border-light dark:border-surface-border-dark rounded-full" />
                <div className="absolute inset-0 border-4 border-brand-600 rounded-full border-t-transparent animate-spin" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  {t('verifyEmail.loading.title')}
                </h2>
                <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-2">
                  {t('verifyEmail.loading.subtitle')}
                </p>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div
              role="status"
              aria-live="polite"
              className="space-y-6 animate-in slide-in-from-bottom duration-500"
            >
              <div className="w-20 h-20 bg-state-ok-bg-light dark:bg-state-ok-bg-dark rounded-full flex items-center justify-center mx-auto text-state-ok-light dark:text-state-ok-dark">
                <CheckCircle2 size={48} strokeWidth={2.5} />
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  {t('verifyEmail.success.title')}
                </h2>
                <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-2">
                  {message || t('verifyEmail.successMessage')}
                </p>
              </div>
              <div className="pt-4 flex items-center justify-center gap-2 text-brand-600 dark:text-brand-400 font-black uppercase tracking-widest text-[10px]">
                <RefreshCw size={12} className="animate-spin" />{' '}
                {t('verifyEmail.success.redirecting')}
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6 animate-in slide-in-from-bottom duration-500">
              <div className="w-20 h-20 bg-state-danger-bg-light dark:bg-state-danger-bg-dark rounded-full flex items-center justify-center mx-auto text-state-danger-light dark:text-state-danger-dark">
                <XCircle size={48} strokeWidth={2.5} />
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  {t('verifyEmail.error.title')}
                </h2>
                <p
                  role="alert"
                  aria-live="assertive"
                  className="text-text-muted-light dark:text-text-muted-dark font-medium mt-2"
                >
                  {message}
                </p>
              </div>

              <div className="space-y-5">
                <Button className="min-h-11 w-full" onClick={() => navigate('/login')}>
                  {t('verifyEmail.error.backToLogin')} <ArrowRight size={16} className="ml-2" />
                </Button>

                <form
                  onSubmit={handleResendEmail}
                  className="space-y-3 border-t border-surface-border-light pt-5 text-left dark:border-surface-border-dark"
                >
                  <label htmlFor="resend-verification-email" className="app-field-label">
                    {t('verifyEmail.resendForm.emailLabel')}
                  </label>
                  <input
                    id="resend-verification-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={resendEmail}
                    disabled={resendStatus === 'sending'}
                    onChange={(event) => setResendEmail(event.target.value)}
                    className="app-field-control min-h-11 w-full"
                    placeholder={t('verifyEmail.resendForm.emailPlaceholder')}
                  />
                  <Button
                    variant="outline"
                    className="min-h-11 w-full"
                    type="submit"
                    isLoading={resendStatus === 'sending'}
                  >
                    {resendStatus === 'sending'
                      ? t('verifyEmail.resendForm.sending')
                      : t('verifyEmail.resendButton')}
                  </Button>
                  {resendStatus === 'sent' && (
                    <p
                      role="status"
                      className="text-sm text-state-ok-light dark:text-state-ok-dark"
                    >
                      {t('verifyEmail.resendForm.sent')}
                    </p>
                  )}
                  {resendStatus === 'error' && (
                    <p
                      role="alert"
                      className="text-sm text-state-danger-light dark:text-state-danger-dark"
                    >
                      {t('verifyEmail.resendForm.failed')}
                    </p>
                  )}
                </form>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
